import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps,
} from "components/AsyncAutocompleteWithQuery";
import {
  BaseFilter,
  CourseTitleModel,
  ListRequestOfBaseFilter,
  ListResponseOfCourseTitleModel,
} from "services/KnowfullyAdminClient";

type SelectCourseTitleProps = {
  value: CourseTitleModel | null;
  onChange: (courseTitle: CourseTitleModel | null) => void;
  cacheKey: string;
  fetchCourseTitles: (
    request: ListRequestOfBaseFilter,
  ) => Promise<ListResponseOfCourseTitleModel>;
} & Omit<
  AsyncAutocompleteWithQueryProps<CourseTitleModel>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const SelectCourseTitle = ({
  value,
  onChange,
  cacheKey,
  fetchCourseTitles,
  ...rest
}: SelectCourseTitleProps) => {
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();

  const fetchData = async (searchTerm: string) => {
    const request = new ListRequestOfBaseFilter({
      pageNumber: 0,
      pageSize: 20,
      parameters: new BaseFilter({
        searchText: searchTerm === "" ? undefined : searchTerm,
      }),
    });
    const result = await queryClient.fetchQuery<ListResponseOfCourseTitleModel>(
      ["courseTitles", cacheKey, searchTerm],
      () => fetchCourseTitles(request),
    );

    result.data?.forEach((x) => {
      if (!x.id) x.id = x.name;
    });

    return { result, forSearchTerm: searchTerm };
  };

  return (
    <AsyncAutocompleteWithQuery
      {...rest}
      value={value}
      labelField="name"
      idField="id"
      fetchData={fetchData}
      failMessage={t({ id: "selectCourseTitleFetchFailed" })}
      onChange={onChange}
    />
  );
};

export default SelectCourseTitle;
